import React, { useState, useEffect } from "react";
import { Accordion } from "react-bootstrap";
import { Spinner } from "../../Common";
import { Button } from "../../Common";
import InputBox from "../../Common/InputBox";
import styles from "./partnerConfig.module.css";
import {
  PartnerConfigList,
  UpdatePartnerConfig,
  getInvestingPartnerDetails,
  UploadDocument,
} from "../../api";
import MultiDropdown from "./MultiDropdown";
import DropdownWithLabelValueAlt from "./DropdownWithLabelValueAlt";
import { CookieUtils } from "../../axios.config";

const PartnerConfig = ({ partnerCd, onBack, refreshPartnerData }) => {
  const [configData, setConfigData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formData, setFormData] = useState({});
  const [modules, setModules] = useState([]);
  const [partnerSpecificData, setPartnerSpecificData] = useState(null);
  const [openSections, setOpenSections] = useState({});
  const [completedModules, setCompletedModules] = useState({});
  const [partnerBasic, setPartnerBasic] = useState({});
  const [partnerTheme, setPartnerTheme] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isFirstTimeConfig, setIsFirstTimeConfig] = useState(false);
  const [accessibleModules, setAccessibleModules] = useState(new Set());
  const [uploadingField, setUploadingField] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});

  useEffect(() => {
    fetchConfigData();
  }, [partnerCd]);

  const getCurrentUsername = () => {
    const cookieUserName = CookieUtils.get("username");
    if (cookieUserName) {
      try {
        return JSON.parse(cookieUserName);
      } catch {
        return cookieUserName;
      }
    }
    return "";
  };

  const normalizeHostUrl = (host) => {
    const raw = String(host || "").trim();
    if (!raw) return "";
    const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return withProtocol.replace(/\/+$/, "");
  };

  const handleBlur = (keyName, e) => {
    const item = formData[keyName];
    if (!item) return;

    // const inputValue = e.target.value;

    // if (!item.isRequired && inputValue.trim() === "") {
    //   handleInputChange(keyName, item.defaultValue ?? "");
    // }
  };
  const isFieldValid = (item, value) => {
    if (!item.isRequired) return true;
    if (value === "" || value === null || value === undefined) {
      return false;
    }
    if (Array.isArray(value) && value.length === 0) {
      return false;
    }
    return true;
  };
  const isValidFileType = (file, allowedTypes = ["jpg", "jpeg", "svg", "gif", "ico", "png"]) => {
    if (!file) return false;    
    const fileExtension = file.name.split('.').pop().toLowerCase();
    return allowedTypes.includes(fileExtension);
  };

  const updateAccessibleModules = (
    completedModulesState,
    modulesList,
    isFirstTime
  ) => {
    const allModulesCompleted = modulesList.every(
      (module) => completedModulesState[module]
    );

    if (!isFirstTime && allModulesCompleted) {
      setAccessibleModules(new Set(modulesList));
      return;
    }

    const accessible = new Set();
    let foundIncomplete = false;

    for (let i = 0; i < modulesList.length; i++) {
      const module = modulesList[i];

      if (!foundIncomplete) {
        accessible.add(module);

        if (!completedModulesState[module]) {
          foundIncomplete = true;
        }
      }
    }

    setAccessibleModules(accessible);
  };

  const fetchConfigData = async () => {
    setLoading(true);
    try {
      const configResponse = await PartnerConfigList("partner");
      const partnerResponse = await getInvestingPartnerDetails(partnerCd);

      if (configResponse.status === 200 && configResponse.data?.data) {
        const templateData = configResponse.data.data;
        setConfigData(templateData);

        const moduleNames = Object.keys(templateData).filter((key) =>
          Array.isArray(templateData[key])
        );
        setModules(moduleNames);

        const initialOpenSections = {};
        const currentValues = {};
        const savedModules = {};
        let alreadyConfiguration = false;
        let partnerLogoUrl = "";
        let partnerHostUrl = "";

        if (partnerResponse.status === 200 && partnerResponse.data?.data) {
          const partnerData = partnerResponse.data.data;
          setPartnerSpecificData(partnerData);
          setPartnerBasic(partnerData.partnerBasic || {});
          setPartnerTheme(partnerData.partnerTheme || []);
          partnerLogoUrl = partnerData?.partnerBasic?.logo ?? "";
          partnerHostUrl = normalizeHostUrl(partnerData?.partnerBasic?.host);

          const allConfigs = [
            ...(partnerData.partnerConfig || []),
            ...(partnerData.partnerTheme || []),
          ];
          alreadyConfiguration = allConfigs.length > 0;

          const configsByModule = {};
          allConfigs.forEach((config) => {
            currentValues[config.key] = config.value;

            const moduleForKey = moduleNames.find((m) =>
              templateData[m].some((item) => item.keyName === config.key)
            );

            if (moduleForKey) {
              if (!configsByModule[moduleForKey]) {
                configsByModule[moduleForKey] = [];
              }
              configsByModule[moduleForKey].push(config);
            }
          });

          moduleNames.forEach((module) => {
            savedModules[module] = !!configsByModule[module]?.length;
          });
        }

        const isFirstTime = !alreadyConfiguration;
        setIsFirstTimeConfig(isFirstTime);
        setCompletedModules(savedModules);
        const allModulesCompleted = moduleNames.every(
          (module) => savedModules[module]
        );
        if (isFirstTime || !allModulesCompleted) {
          initialOpenSections[moduleNames[0]] = true;
        } else {
          moduleNames.forEach((module, index) => {
            initialOpenSections[module] = index === 0;
          });
        }
        setOpenSections(initialOpenSections);
        updateAccessibleModules(savedModules, moduleNames, isFirstTime);

        const initialFormData = {};
        moduleNames.forEach((module) => {
          if (Array.isArray(templateData[module])) {
            templateData[module].forEach((item) => {
              const itemKey = item.keyName;
              const isBranding = module === "branding";
              const isPlatform = module === "platform";

              const shouldPrefillLogo =
                isBranding &&
                partnerLogoUrl &&
                ["shortLogo", "siteLogo"].includes(itemKey);

              const shouldPrefillPlatformUrl =
                isPlatform &&
                partnerHostUrl && 
                ["url"].includes(itemKey);

              const shouldPrefillDigiLockerRedirectUrl =
                isPlatform &&
                partnerHostUrl &&
                ["digiLockerFailureRedirectUrl", "digiLockerSuccessRedirectUrl"].includes(itemKey);
 
              let derivedDefault = item.defaultValue;
              if (shouldPrefillLogo) {
                derivedDefault = partnerLogoUrl;
              } else if (shouldPrefillPlatformUrl) {
                derivedDefault = partnerHostUrl;
              } else if (shouldPrefillDigiLockerRedirectUrl) {
                derivedDefault = `${partnerHostUrl}/redirecting`;
              }

              initialFormData[item.keyName] = {
                value:
                  currentValues[item.keyName] !== undefined
                    ? currentValues[item.keyName]
                    : derivedDefault,
                defaultValue: derivedDefault,
                sourceTable: item.sourceTable,
                module: item.moduleName,
                fieldType: item.fieldType,
                isRequired: item.isRequired,
                isEditable: item.isEditable,
              };
            });
          }
        });

        setFormData(initialFormData);
      } else {
        setError("Failed to fetch configuration data");
      }
    } catch (err) {
      setError("Error loading configuration data");
    } finally {
      setLoading(false);
    }
  };

  const refreshPartnerDetails = async () => {
    try {
      const response = await getInvestingPartnerDetails(partnerCd);
      if (response.status === 200 && response.data) {
        setPartnerSpecificData(response.data.data);
        if (typeof refreshPartnerData === "function") {
          refreshPartnerData(response.data.data);
        }
        const savedModules = {};
        if (
          response.data.data.partnerConfig &&
          Array.isArray(response.data.data.partnerConfig)
        ) {
          const configsByModule = {};
          response.data.data.partnerConfig.forEach((config) => {
            const moduleForKey = modules.find((m) =>
              configData[m].some((item) => item.keyName === config.key)
            );

            if (moduleForKey) {
              if (!configsByModule[moduleForKey]) {
                configsByModule[moduleForKey] = [];
              }
              configsByModule[moduleForKey].push(config);
            }
          });
          modules.forEach((module) => {
            savedModules[module] = !!configsByModule[module]?.length;
          });
        }
        setCompletedModules(savedModules);
        updateAccessibleModules(savedModules, modules, isFirstTimeConfig);
      }
    } catch (err) {}
  };

  // const handleInputChange = (keyName, value) => {
  //   setFormData((prev) => {
  //     const updated = { ...prev };
  //     if (value === "") {
  //       delete updated[keyName];
  //     } else {
  //       updated[keyName] = {
  //         ...prev[keyName],
  //         value,
  //       };
  //     }
  //     return updated;
  //   });
  //   if (fieldErrors[keyName]) {
  //     const item = configData[formData[keyName]?.module]?.find(
  //       (field) => field.keyName === keyName
  //     );
      
  //     if (item && isFieldValid(item, value)) {
  //       setFieldErrors((prev) => {
  //         const updated = { ...prev };
  //         delete updated[keyName];
  //         return updated;
  //       });
  //     }
  //   }
  //   if (uploadErrors[keyName] && value) {
  //     setUploadErrors((prev) => {
  //       const updated = { ...prev };
  //       delete updated[keyName];
  //       return updated;
  //     });
  //   }
  // };
  const handleInputChange = (keyName, value) => {
  setFormData((prev) => {
    const updated = { ...prev };
    updated[keyName] = {
      ...prev[keyName],
      value, // empty bhi ho to overwrite kar do
    };
    return updated;
  });

  if (fieldErrors[keyName]) {
    const item = configData[formData[keyName]?.module]?.find(
      (field) => field.keyName === keyName
    );

    if (item && isFieldValid(item, value)) {
      setFieldErrors((prev) => {
        const updated = { ...prev };
        delete updated[keyName];
        return updated;
      });
    }
  }

  if (uploadErrors[keyName] && value) {
    setUploadErrors((prev) => {
      const updated = { ...prev };
      delete updated[keyName];
      return updated;
    });
  }
};

  const toggleSection = (module) => {
    const allModulesCompleted = modules.every((m) => completedModules[m]);
    const inSequentialMode = isFirstTimeConfig || !allModulesCompleted;
    if (inSequentialMode && !accessibleModules.has(module)) {
      return;
    }
    setOpenSections((prev) => {
      const isOpen = prev[module];
      if (inSequentialMode) {
        const newOpenSections = {};
        newOpenSections[module] = !isOpen;
        return newOpenSections;
      } else {
        return {
          ...prev,
          [module]: !isOpen,
        };
      }
    });
    setError("");
    setSuccess("");
  };

  const handleSaveConfig = async (module) => {
    setSaving((prev) => ({ ...prev, [module]: true }));
    setError((prev) => ({ ...prev, [module]: "" }));
    setSuccess((prev) => ({ ...prev, [module]: "" }));
    const newFieldErrors = {};
    const moduleFields = configData[module] || [];
    moduleFields.forEach((item) => {
      const value = formData[item.keyName]?.value;
      if (!isFieldValid(item, value)) {
        newFieldErrors[item.keyName] = `${
          item.description || item.keyName
        } is required`;
      }
    });

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setSaving((prev) => ({ ...prev, [module]: false }));
      return;
    }
    setFieldErrors({});
    try {
      const configs = Object.keys(formData).map((keyName) => {
        const item = formData[keyName];
        let value = item.value;
        const fieldType = item.fieldType?.toLowerCase();

        switch (fieldType) {
          case "boolean":
            value = value === true || value === "true";
            break;
          case "number":
            value = parseFloat(value);
            break;
          case "array":
          case "multi-dropdown":
            value = Array.isArray(value)
              ? value.map((v) => String(v))
              : typeof value === "string"
              ? value.split(",").map((v) => v.trim())
              : [];
            break;
          case "json":
          case "dropdownwithlabelvaluealt":
            try {
              value = typeof value === "string" ? JSON.parse(value) : value;
            } catch {}
            break;
        }

        if (keyName.toLowerCase() === "status" && typeof value === "string") {
          value = value.toLowerCase();
        }

        if (
          value === "" ||
          value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0)
        ) {
          value = item.defaultValue ?? "";
        }

        return {
          key: keyName,
          value,
          source_table: item.sourceTable,
        };
      });
      const payload = {
        partnerCd,
        module: module.toLowerCase(),
        updatedBy: getCurrentUsername(),
        status: "ACTIVE",
        configs: configs.filter(
          (config) =>
            formData[config.key].module.toLowerCase() === module.toLowerCase()
        ),
      };

      const response = await UpdatePartnerConfig(payload);

      if (response.status === 200) {
        setSuccess((prev) => ({
          ...prev,
          [module]: `${module} configuration saved successfully`,
        }));
        await refreshPartnerDetails();
        const newCompletedModules = { ...completedModules, [module]: true };
        setCompletedModules(newCompletedModules);
        updateAccessibleModules(newCompletedModules, modules, isFirstTimeConfig);

        setTimeout(() => {
          setSuccess((prev) => ({ ...prev, [module]: "" }));
          const allModulesCompleted = modules.every(
            (m) => newCompletedModules[m]
          );
          const inSequentialMode = isFirstTimeConfig || !allModulesCompleted;
          if (inSequentialMode) {
            const currentIndex = modules.indexOf(module);
            if (currentIndex < modules.length - 1) {
              const nextModule = modules[currentIndex + 1];
              setOpenSections({ [nextModule]: true });
            } else {
              if (allModulesCompleted) {
                setIsFirstTimeConfig(false);
                updateAccessibleModules(newCompletedModules, modules, false);
              }
              onBack();
            }
          } else {
            setOpenSections((prev) => ({ ...prev, [module]: false }));
          }
        }, 2000);
      } else {
        setError((prev) => ({
          ...prev,
          [module]: response.data?.message || "Failed to save configuration",
        }));
      }
    } catch {
      setError((prev) => ({
        ...prev,
        [module]: "Error saving configuration",
      }));
    } finally {
      setSaving((prev) => ({ ...prev, [module]: false }));
    }
  };
  const handleFileUpload = async (e, handleInputChange, item, setError) => {
    const file = e.target.files[0];
    if (!file) return;

    const key = item.keyName;
    setUploadErrors((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
    if (!isValidFileType(file)) {
      const errorMsg = "Only jpg, jpeg, svg, gif, ico and png files are allowed";
      setUploadErrors((prev) => ({
        ...prev,
        [key]: errorMsg,
      }));
      return;
    }
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    setUploadingField((prev) => ({ ...prev, [key]: true }));
    try {
      const response = await UploadDocument(uploadFormData);
      if (response?.status === 200 && response?.data?.data) {
        handleInputChange(key, response.data.data);
        setError("");
        if (fieldErrors[key]) {
          setFieldErrors((prev) => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
          });
        }
      } else {
        const msg = response?.data?.message || "Upload failed.";
        setUploadErrors((prev) => ({ ...prev, [key]: msg }));
        setError(msg);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || "Something went wrong.";
      setUploadErrors((prev) => ({ ...prev, [key]: msg }));
      setError(msg);
    }
    setUploadingField((prev) => ({ ...prev, [key]: false }));
  };

  const renderFormField = (item) => {
    const value = formData[item.keyName]?.value ?? item.defaultValue;

    const disabled = item.isEditable === false;

    switch (item.fieldType.toLowerCase()) {
      case "boolean":
        const booleanValue = String(formData[item.keyName]?.value).toLowerCase();
        return (
          <select
            className={styles.selectField}
            value={booleanValue === "true" ? "true" : "false"}
            onChange={(e) =>
              handleInputChange(item.keyName, e.target.value === "true")
            }
            onBlur={(e) => handleBlur(item.keyName, e)}
            disabled={disabled}
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        );

      case "color":
        return (
          <div className={styles.colorPickerContainer}>
            <input
              type="color"
              value={value}
              onChange={(e) => handleInputChange(item.keyName, e.target.value)}
              disabled={disabled}
            />
            <InputBox
              value={value}
              onChange={(e) => handleInputChange(item.keyName, e.target.value)}
              onBlur={(e) => handleBlur(item.keyName, e)}
              placeholder="Enter color code"
              disabled={disabled}
            />
          </div>
        );

      case "json":
        return (
          <textarea
            className={styles.textArea}
            value={
              typeof value === "object" ? JSON.stringify(value, null, 2) : value
            }
            onChange={(e) => handleInputChange(item.keyName, e.target.value)}
            onBlur={(e) => handleBlur(item.keyName, e)}
            rows={4}
            disabled={disabled}
          />
        );

      case "html":
      case "css":
      case "text":
        return (
          <textarea
            className={styles.textArea}
            value={value}
            onChange={(e) => handleInputChange(item.keyName, e.target.value)}
            onBlur={(e) => handleBlur(item.keyName, e)}
            rows={4}
            disabled={disabled}
          />
        );
      case "url":
        return (
          <InputBox
            type="url"
            value={value}
            onChange={(e) => handleInputChange(item.keyName, e.target.value)}
            onBlur={(e) => handleBlur(item.keyName, e)}
            placeholder="Enter URL"
            disabled={disabled}
          />
        );
      case "upload":
        const key = item.keyName;
        return (
          <div className={styles.uploadWrapper}>
            <input
              type="text"
              className={styles.urlInput}
              value={value || ""}
              disabled
              placeholder="Uploaded file URL"
            />

            <div className={styles.buttonGroup}>
              {uploadingField[key] ? (
                <Button loading={true} title="Uploading..." disabled />
              ) : (
                <label
                  className={`${styles.uploadBtn} ${
                    disabled ? styles.disabled : ""
                  }`}
                >
                  Choose File
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.pdf"
                    onChange={(e) =>
                      handleFileUpload(e, handleInputChange, item, setError)
                    }
                    className={styles.hiddenInput}
                    disabled={disabled}
                  />
                </label>
              )}
              {value && (
                <a
                  href={value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.viewBtn}
                >
                  View File
                </a>
              )}
            </div>
            {uploadErrors[key] && (
              <p style={{ color: "red", marginTop: "5px" }}>
                {uploadErrors[key]}
              </p>
            )}
          </div>
        );
      case "email":
        return (
          <InputBox
            type="email"
            value={value}
            onChange={(e) => handleInputChange(item.keyName, e.target.value)}
            onBlur={(e) => handleBlur(item.keyName, e)}
            placeholder="Enter email address"
            disabled={disabled}
          />
        );

      case "number":
        return (
          <InputBox
            type="number"
            value={value}
            onChange={(e) =>
              handleInputChange(item.keyName, parseFloat(e.target.value))
            }
            onBlur={(e) => handleBlur(item.keyName, e)}
            placeholder="Enter number"
            disabled={disabled}
          />
        );

      case "array":
        return (
          <textarea
            className={styles.textArea}
            value={Array.isArray(value) ? value.join(", ") : value}
            onChange={(e) => {
              const arr = e.target.value.split(",").map((str) => str.trim());
              handleInputChange(item.keyName, arr);
            }}
            onBlur={(e) => handleBlur(item.keyName, e)}
            placeholder="Enter comma-separated values"
            rows={3}
            disabled={disabled}
          />
        );

      case "multi-dropdown":
        const options = item.allowedValues || [];
        return (
          <MultiDropdown
            options={options}
            value={
              Array.isArray(formData[item.keyName]?.value)
                ? formData[item.keyName].value
                : formData[item.keyName]?.defaultValue || []
            }
            onChange={(selectedOptions) =>
              handleInputChange(item.keyName, selectedOptions)
            }
            onBlur={(e) => handleBlur(item.keyName, e)}
            placeholder={`Select ${item.description || item.keyName}`}
            disabled={disabled}
          />
        );

      case "dropdownwithlabelvaluealt":
        return (
          <DropdownWithLabelValueAlt
            value={value}
            onChange={(newValue) => handleInputChange(item.keyName, newValue)}
            onBlur={(e) => handleBlur(item.keyName, e)}
            disabled={false}
            inputsDisabled={disabled}
            title={item.description || item.keyName}
            allowedValues={item.allowedValues || []}
            placeholder={{
              label: "Enter label",
              value: "Enter value",
              alt: "Enter alt text",
            }}
          />
        );

      case "dropdown":
        return (
          <select
            className={styles.selectField}
            value={formData[item.keyName]?.value || ""}
            onChange={(e) => handleInputChange(item.keyName, e.target.value)}
            onBlur={(e) => handleBlur(item.keyName, e)}
            disabled={disabled}
          >
            <option value="">--select--</option>
            {item.allowedValues?.map((option, idx) => (
              <option
                key={idx}
                value={option}
                className={styles.dropOption}
              >
                {option === "NA" ? "Not Applicable" : option}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <InputBox
            value={value}
            onChange={(e) => handleInputChange(item.keyName, e.target.value)}
            onBlur={(e) => handleBlur(item.keyName, e)}
            placeholder={`Enter ${item.description || item.keyName}`}
            disabled={disabled}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles.configContainer}>
      <div className={styles.header}>
        <h4>Partner Configuration: {partnerCd}</h4>
      </div>

      {modules.length === 0 ? (
        <div className={styles.noData}>No configuration modules available</div>
      ) : (
        <Accordion className={styles.accordionContainer}>
          {modules.map((module, index) => {
            const isAccessible = accessibleModules.has(module);
            return (
              <div
                key={module}
                className={`${styles.accordionItem} ${
                  !isAccessible ? styles.lockedModule : ""
                }`}
              >
                <div
                  className={`${styles.accordionHeader} ${
                    !isAccessible ? styles.disabledHeader : ""
                  }`}
                  onClick={() => toggleSection(module)}
                  style={{
                    cursor: isAccessible ? "pointer" : "not-allowed",
                    opacity: isAccessible ? 1 : 0.6,
                  }}
                >
                  <div className={styles.moduleTitle}>
                    <span>{module}</span>
                  </div>
                  <span className={styles.toggleIcon}>
                    {openSections[module] ? "−" : "+"}
                  </span>
                </div>

                {openSections[module] && isAccessible && (
                  <div className={styles.accordionContent}>
                    <div className={styles.formGrid}>
                      {configData[module] &&
                        configData[module].map((item) => (
                          <div
                            className={
                              item.fieldType.toLowerCase() ===
                              "dropdownwithlabelvaluealt"
                                ? styles.fullRowGroup
                                : styles.formGroup
                            }
                            key={item.id}
                          >
                            <label className={styles.label}>
                              {item.description || item.keyName}
                              {item.isRequired && (
                                <span className={styles.required}>*</span>
                              )}
                            </label>
                            {renderFormField(item)}
                            {item.comment && (
                              <p className={styles.comment}>{item.comment}</p>
                            )}
                            {fieldErrors[item.keyName] && !uploadErrors[item.keyName] && (
                              <div className={styles.fieldError}>
                                {fieldErrors[item.keyName]}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>

                    {error[module] && (
                      <div className={styles.errorMessage}>{error[module]}</div>
                    )}
                    {success[module] && (
                      <div className={styles.successMessage}>
                        {success[module]}
                      </div>
                    )}

                    <div className={styles.actions}>
                      <Button
                        title="Save Configuration"
                        onClick={() => handleSaveConfig(module)}
                        disabled={saving[module]}
                        loading={saving[module]}
                      />
                    </div>
                  </div>
                )}
                <hr className={styles.divider} />
              </div>
            );
          })}
        </Accordion>
      )}
    </div>
  );
};

export default PartnerConfig;